import {
  type AccountData,
  type AuditLog,
  getWorkflowAccountTimeline,
  isDefined,
  isNotDefined,
  type LatLng,
  type UserId,
  type Workflow,
  type WorkflowAction,
  WORKFLOW_PROSPECT_STAGE_ID,
  type WorkflowStage,
} from '@terros/common'
import { type AccountHistorySource, findHistorySource } from './historySource'
import { groupHistoryBatches, type IndexedHistoryItem } from './historyBatch'

export type AccountJournalEntry = {
  timestamp: number
  userId: UserId
  latlng?: LatLng
  stage: WorkflowStage
  isStageChange: boolean
  fromStage?: WorkflowStage
  passedStages: WorkflowStage[]
  action?: WorkflowAction
  preActions: WorkflowAction[]
  sourceStatus?: string
  source?: AccountHistorySource
}

type BuildAccountJournalProps = {
  workflow: Workflow
  account: Pick<AccountData, 'workflowHistory' | 'importId' | 'modifiedBy'> | undefined
}

/** One entry per account update, oldest first */
export const buildAccountJournal = ({ workflow, account }: BuildAccountJournalProps): AccountJournalEntry[] => {
  if (isNotDefined(account)) return []
  const initialStage = isDefined(account.importId) ? undefined : workflow.getStage(WORKFLOW_PROSPECT_STAGE_ID)
  return groupHistoryBatches({ timeline: getWorkflowAccountTimeline(account), workflow }).reduce<AccountJournalEntry[]>(
    (acc, batch) => {
      const previousStage = acc.at(-1)?.stage ?? initialStage
      const entry = buildJournalEntry({ workflow, batch, previousStage, modifiedBy: account.modifiedBy })
      if (isDefined(entry)) acc.push(entry)
      return acc
    },
    []
  )
}

type BuildJournalEntryProps = {
  workflow: Workflow
  batch: IndexedHistoryItem[]
  previousStage: WorkflowStage | undefined
  modifiedBy: AuditLog[] | undefined
}

const buildJournalEntry = ({
  workflow,
  batch,
  previousStage,
  modifiedBy,
}: BuildJournalEntryProps): AccountJournalEntry | undefined => {
  const last = batch.at(-1)
  if (isNotDefined(last)) return
  const stage = workflow.getStage(last.stageId)
  if (isNotDefined(stage)) return

  const isStageChange = previousStage?.id !== stage.id
  return {
    timestamp: last.timestamp,
    userId: last.userId,
    latlng: last.latlng,
    stage,
    isStageChange,
    fromStage: isStageChange ? previousStage : undefined,
    passedStages: isStageChange ? getPassedStages({ workflow, from: previousStage, to: stage }) : [],
    action: workflow.getAction(last.actionId),
    preActions: batch
      .slice(0, -1)
      .map((item) => workflow.getAction(item.actionId))
      .filter(isDefined),
    sourceStatus: last.sourceStatus,
    source: findHistorySource({ modifiedBy, to: last.timestamp }),
  }
}

type GetPassedStagesProps = {
  workflow: Workflow
  from: WorkflowStage | undefined
  to: WorkflowStage
}

/** Pipeline stages skipped when moving forward, e.g. Knock -> Lead passes Pitch */
const getPassedStages = ({ workflow, from, to }: GetPassedStagesProps): WorkflowStage[] => {
  if (!to.isPipelineStage || to.isInitial) return []
  // ponytail: leaving a non-pipeline stage says nothing about which pipeline stages were passed
  if (isDefined(from) && !from.isInitial && !from.isPipelineStage) return []
  const fromOrder = isNotDefined(from) || from.isInitial ? -Infinity : from.order
  return workflow.pipelineStages.filter((s) => !s.isInitial && s.order > fromOrder && s.order < to.order)
}

/**
 * Whether the action is worth naming on a stage move. Most actions share their target stage's name
 * ("Sold" moves to Sold), and saying it twice reads oddly; the pin icon already shows the action.
 */
export const isNamedAction = ({ action, stage }: AccountJournalEntry): boolean => {
  if (isNotDefined(action)) return false
  return action.name.trim().toLowerCase() !== stage.name.trim().toLowerCase()
}
